import React, { useLayoutEffect, useRef, useState } from 'react';
import { Render, createUsePuck } from '@puckeditor/core';
import { useThemeConfiguration } from '../context/ThemeConfigurationContext';
import { styledElementBlock } from './elementSamples';
import SitePreviewScope from './SitePreviewScope';
import classes from './StyleCardPreview.module.css';

// Largeur où le bloc est rendu avant d'être ajusté à la vignette : étroite,
// pour qu'il la remplisse au lieu d'y paraître perdu. Le carrousel prend plus
// large : une slide de galerie fait au moins 200 px, trois doivent tenir ;
// le bouton, plus étroit : il ne prend que la largeur de son libellé.
const PAGE_WIDTHS = { carousel: 720, button: 220 };
const DEFAULT_PAGE_WIDTH = 320;

const usePuckApi = createUsePuck();

/** Le bloc ajusté à sa vignette : le plus grand possible sans en sortir,
 *  centré. */
function useFit(frameRef, pageRef, pageWidth) {
  const [fit, setFit] = useState(null);
  useLayoutEffect(() => {
    const frame = frameRef.current;
    const page = pageRef.current;
    if (!frame || !page || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => {
      // Un bloc plus large que sa page (un bouton au long libellé) déborde :
      // on ajuste sur sa largeur réelle, sinon il sortirait de la vignette.
      const width = Math.max(pageWidth, page.scrollWidth);
      const height = page.offsetHeight || 1;
      const scale = Math.min(frame.clientWidth / width, frame.clientHeight / height);
      setFit({
        scale,
        left: (frame.clientWidth - width * scale) / 2,
        top: (frame.clientHeight - height * scale) / 2,
      });
    };
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    observer.observe(page);
    measure();
    return () => observer.disconnect();
  }, [frameRef, pageRef, pageWidth]);
  return fit;
}

/**
 * Le vrai bloc d'un style (galerie de l'inspecteur, thème ou génériques),
 * ajusté à la tuile : ses couleurs, sa forme et ses textes tels que le site
 * les rendra, avec le thème du site.
 *
 * @param {{ kind: string, values: object }} props `kind` : composant du thème
 *   (cf. puck/themedBlocks) ; `values` : les valeurs que le style pose.
 */
export default function StyleCardPreview({ kind, values }) {
  const configuration = useThemeConfiguration() || {};
  // La config de l'éditeur où la galerie est montée : importer celle du site
  // ferait un cycle (elle déclare les galeries).
  const config = usePuckApi((s) => s.config);
  const frameRef = useRef(null);
  const pageRef = useRef(null);
  const pageWidth = PAGE_WIDTHS[kind] || DEFAULT_PAGE_WIDTH;
  const fit = useFit(frameRef, pageRef, pageWidth);

  return (
    <div ref={frameRef} className={classes.frame} aria-hidden>
      <div
        ref={pageRef}
        className={classes.page}
        style={{
          width: pageWidth,
          left: fit?.left ?? 0,
          top: fit?.top ?? 0,
          transform: `scale(${fit?.scale ?? 0})`,
          visibility: fit ? undefined : 'hidden',
        }}
      >
        <SitePreviewScope configuration={configuration}>
          <Render
            config={config}
            data={{ root: { props: {} }, content: [styledElementBlock(kind, values, config.components)] }}
            metadata={{ themeConfiguration: configuration }}
          />
        </SitePreviewScope>
      </div>
    </div>
  );
}

/** Cadre d'une tuile sans style à montrer (« Personnalisé ») : le même que
 *  celui des aperçus, avec une icône. */
export function StyleCardPlaceholder({ children }) {
  return (
    <div className={`${classes.frame} ${classes.placeholder}`} aria-hidden>{children}</div>
  );
}
